/**
 * SessionProgress — Compact exchange progress indicator.
 *
 * Shows exchange count, remaining, progress bar, and phase-aware color.
 * Sits in the chat header for in-progress sessions.
 */

import type { SessionContractInfo } from '../api/client';

interface Props {
	contract: SessionContractInfo;
	mode?: 'canonical' | 'private';
}

export function SessionProgress({ contract, mode }: Props) {
	const { budget, exchangeCount, phase, exchangesRemaining } = contract;
	const progress = Math.min(1, exchangeCount / budget);
	const isPrivate = mode === 'private';

	const phaseColor =
		phase === 'consolidation' ? 'var(--color-warning, #f5a623)' :
		phase === 'wrap_up' || phase === 'grace' ? 'var(--color-success, #22c55e)' :
		'var(--color-text-muted)';

	// Canonical: hard count + short phase label.
	// Private: softer phase-tone only, no count — reflection shouldn't feel budgeted.
	const phaseLabel = isPrivate
		? (
			phase === 'working' ? 'Plenty of space'
			: phase === 'consolidation' ? 'Partway through'
			: phase === 'wrap_up' ? 'Winding down'
			: phase === 'grace' ? 'Closing'
			: null
		)
		: (
			phase === 'wrap_up' ? 'Wrapping up'
			: phase === 'grace' ? 'Follow-up'
			: null
		);

	return (
		<div class="session-progress">
			<div class="session-progress__info">
				{isPrivate ? (
					phaseLabel && (
						<span class="session-progress__phase" style={{ color: phaseColor }}>
							{phaseLabel}
						</span>
					)
				) : (
					<>
						<span class="session-progress__count">
							Exchange <span class="session-progress__num">{exchangeCount}</span>
						</span>
						<span class="session-progress__separator">&middot;</span>
						<span class="session-progress__remaining">
							<span class="session-progress__num">{exchangesRemaining}</span> remaining
						</span>
						{phaseLabel && (
							<>
								<span class="session-progress__separator">&middot;</span>
								<span class="session-progress__phase" style={{ color: phaseColor }}>
									{phaseLabel}
								</span>
							</>
						)}
					</>
				)}
			</div>
			<div class="session-progress__bar">
				<div
					class="session-progress__fill"
					style={{
						width: `${progress * 100}%`,
						background: phaseColor,
					}}
				/>
			</div>
			<style>{progressStyles}</style>
		</div>
	);
}

const progressStyles = `
	/*
	 * The progress bar doubles as the visual divider between the session
	 * subheader (info text) and the chat window below. No separate
	 * border-bottom — the 2px track + colored fill IS the border.
	 */
	.session-progress {
		padding: 0.5rem 1.5rem 0;
		background: var(--color-bg-elevated);
		flex-shrink: 0;
	}

	.session-progress__info {
		display: flex;
		align-items: center;
		gap: 0.375rem;
		font-family: var(--font-mono);
		font-size: 0.6875rem;
		color: var(--color-text-muted);
		margin-bottom: 0.375rem;
	}

	.session-progress__num {
		font-weight: 600;
		color: var(--color-text);
	}

	.session-progress__separator {
		color: var(--color-text-subtle);
	}

	.session-progress__phase {
		font-weight: 500;
	}

	.session-progress__bar {
		height: 2px;
		background: var(--color-border);
		overflow: hidden;
		/* Full-bleed: cancel container padding so the bar spans edge-to-edge as a divider. */
		margin: 0 -1.5rem;
	}

	.session-progress__fill {
		height: 100%;
		transition: width 300ms var(--ease-out-quart), background 300ms;
	}

	@media (max-width: 767px) {
		.session-progress {
			padding: 0.375rem 1rem 0;
		}
		.session-progress__bar {
			margin: 0 -1rem;
		}
	}
`;
